import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { IPopup } from '@/services/popups/types';
import { IMyInfo } from '@/services/user/types';
import { getMockPopups } from '@/services/popups';
import {
  getDisplayablePopups,
  markPopupAsSeenToday,
  cleanupOldPopupRecords,
} from '@/utils/helpers/popup';
import { parseAuthToken } from '@/utils/helpers/token';
import { getAccessToken } from '@/services/axios-instance';

interface UsePopupManagerReturn {
  currentPopup: IPopup | null;
  popupQueue: IPopup[];
  currentIndex: number;
  totalPopups: number;
  isLoading: boolean;
  error: Error | null;
  closePopup: () => void;
  confirmPopup: () => void;
  nextPopup: () => void;
  previousPopup: () => void;
  skipAllPopups: () => void;
}

export const usePopupManager = (
  userInfo: IMyInfo | null
): UsePopupManagerReturn => {
  const [popupQueue, setPopupQueue] = useState<IPopup[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);

  const {
    data: popupResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['popups', 'active'],
    queryFn: getMockPopups,
    enabled: !!userInfo,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!userInfo || !popupResponse?.data || isLoading || isInitialized) {
      return;
    }

    try {
      const token = getAccessToken();
      if (!token) return;

      const parsedToken = parseAuthToken(token);
      const userId = parsedToken.id;

      // Eski popup kayıtlarını temizle
      cleanupOldPopupRecords(userId);

      const displayablePopups = getDisplayablePopups(
        popupResponse.data,
        userInfo,
        userId
      );

      if (displayablePopups.length > 0) {
        setPopupQueue(displayablePopups);
        setCurrentIndex(0);
      }

      setIsInitialized(true);
    } catch (error) {
      console.error('Popup yönetimi hatası:', error);
      setIsInitialized(true);
    }
  }, [userInfo, popupResponse, isLoading, isInitialized]);

  const currentPopup = popupQueue.length > 0 ? popupQueue[currentIndex] : null;

  const closePopup = () => {
    if (!currentPopup) return;

    try {
      setPopupQueue([]);
      setCurrentIndex(0);
    } catch (error) {
      setPopupQueue([]);
      setCurrentIndex(0);
    }
  };

  const confirmPopup = () => {
    if (!currentPopup) return;

    try {
      const token = getAccessToken();
      if (!token) return;

      const parsedToken = parseAuthToken(token);
      const userId = parsedToken.id;

      markPopupAsSeenToday(userId, currentPopup.id);

      if (currentIndex < popupQueue.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        setPopupQueue([]);
        setCurrentIndex(0);
      }
    } catch (error) {
      setPopupQueue([]);
      setCurrentIndex(0);
    }
  };

  const nextPopup = () => {
    if (currentIndex < popupQueue.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const previousPopup = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const skipAllPopups = () => {
    try {
      const token = getAccessToken();
      if (!token) return;

      const parsedToken = parseAuthToken(token);
      const userId = parsedToken.id;

      popupQueue.forEach((popup) => {
        markPopupAsSeenToday(userId, popup.id);
      });

      setPopupQueue([]);
      setCurrentIndex(0);
    } catch (error) {
      console.error('Popup atlama hatası:', error);
      setPopupQueue([]);
      setCurrentIndex(0);
    }
  };

  return {
    currentPopup,
    popupQueue,
    currentIndex,
    totalPopups: popupQueue.length,
    isLoading,
    error: error as Error | null,
    closePopup,
    confirmPopup,
    nextPopup,
    previousPopup,
    skipAllPopups,
  };
};
